import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Users, BookOpen, Package, Gift, DollarSign, ShieldCheck, ArrowLeft, RefreshCw } from 'lucide-react';

const ADMIN_KEY_STORAGE = 'lifeecho_admin_key';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [adminKey, setAdminKey] = useState(localStorage.getItem(ADMIN_KEY_STORAGE) || '');
  const [authenticated, setAuthenticated] = useState(false);
  const [keyInput, setKeyInput] = useState('');
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState('overview');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const headers = { 'X-Admin-Key': adminKey };

  useEffect(() => {
    if (adminKey) verifyKey();
  }, []);

  const verifyKey = async (key) => {
    const k = key || adminKey;
    try {
      const res = await fetch(`${BACKEND}/api/admin/stats`, { headers: { 'X-Admin-Key': k } });
      if (res.ok) { setAuthenticated(true); setStats(await res.json()); }
      else { setAuthenticated(false); localStorage.removeItem(ADMIN_KEY_STORAGE); }
    } catch { setAuthenticated(false); }
  };

  const handleLogin = () => {
    localStorage.setItem(ADMIN_KEY_STORAGE, keyInput);
    setAdminKey(keyInput);
    verifyKey(keyInput);
  };

  const fetchTab = async (tabName) => {
    setTab(tabName);
    if (tabName === 'overview') { verifyKey(); return; }
    setLoading(true);
    try {
      const endpoint = { autobiographies: 'autobiographies', orders: 'orders', gifts: 'gifts', users: 'users' }[tabName];
      const res = await fetch(`${BACKEND}/api/admin/${endpoint}`, { headers });
      if (res.ok) setData(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const formatDate = (d) => { try { return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return '-'; } };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <div className="bg-gray-800 rounded-2xl p-8 w-full max-w-md" data-testid="admin-login">
          <div className="flex items-center gap-3 mb-6">
            <ShieldCheck className="w-8 h-8 text-purple-400" />
            <h1 className="text-2xl font-bold text-white">Admin Access</h1>
          </div>
          <Input value={keyInput} onChange={(e) => setKeyInput(e.target.value)} placeholder="Enter admin key" type="password" className="bg-gray-700 border-gray-600 text-white mb-4" data-testid="admin-key-input" />
          <Button onClick={handleLogin} className="w-full bg-purple-500 hover:bg-purple-600 text-white py-5 rounded-xl" data-testid="admin-login-btn">Access Dashboard</Button>
        </div>
      </div>
    );
  }

  const TABS = [
    { id: 'overview', label: 'Overview', icon: DollarSign },
    { id: 'autobiographies', label: 'Stories', icon: BookOpen },
    { id: 'orders', label: 'Orders', icon: Package },
    { id: 'gifts', label: 'Gifts', icon: Gift },
    { id: 'users', label: 'Users', icon: Users },
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-6 h-6 text-purple-400" />
          <h1 className="text-lg font-bold">Life Echo Admin</h1>
        </div>
        <div className="flex gap-3">
          <Button variant="ghost" size="sm" onClick={() => fetchTab(tab)} className="text-gray-400 hover:text-white"><RefreshCw className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-gray-400 hover:text-white"><ArrowLeft className="w-4 h-4 mr-1" /> Home</Button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <nav className="w-56 bg-gray-800/50 min-h-[calc(100vh-64px)] p-4 space-y-1">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => fetchTab(t.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${tab === t.id ? 'bg-purple-500/20 text-purple-400' : 'text-gray-400 hover:bg-gray-700/50 hover:text-white'}`}
                data-testid={`tab-${t.id}`}
              >
                <Icon className="w-4 h-4" />{t.label}
              </button>
            );
          })}
        </nav>

        {/* Content */}
        <main className="flex-1 p-6">
          {tab === 'overview' && stats && (
            <div className="fade-in" data-testid="admin-overview">
              <h2 className="text-2xl font-bold mb-6">Dashboard Overview</h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {[
                  { label: 'Total Users', value: stats.total_users, icon: Users, color: 'text-blue-400 bg-blue-400/10' },
                  { label: 'Autobiographies', value: stats.total_autobiographies, icon: BookOpen, color: 'text-purple-400 bg-purple-400/10' },
                  { label: 'Hardcover Orders', value: stats.total_orders, icon: Package, color: 'text-amber-400 bg-amber-400/10' },
                  { label: 'Gifts', value: stats.total_gifts, icon: Gift, color: 'text-pink-400 bg-pink-400/10' },
                ].map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <div key={i} className="bg-gray-800 rounded-xl p-5" data-testid={`stat-${s.label.toLowerCase().replace(/ /g, '-')}`}>
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${s.color}`}><Icon className="w-5 h-5" /></div>
                      <p className="text-2xl font-bold">{s.value}</p>
                      <p className="text-sm text-gray-500">{s.label}</p>
                    </div>
                  );
                })}
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-gray-800 rounded-xl p-5">
                  <p className="text-sm text-gray-500 mb-1">Autobiography Revenue</p>
                  <p className="text-3xl font-bold text-green-400">£{stats.autobiography_revenue?.toFixed(0)}</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-5">
                  <p className="text-sm text-gray-500 mb-1">Hardcover Revenue</p>
                  <p className="text-3xl font-bold text-amber-400">£{stats.hardcover_revenue?.toFixed(0)}</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-5 border border-purple-500/30">
                  <p className="text-sm text-gray-500 mb-1">Total Revenue</p>
                  <p className="text-3xl font-bold text-purple-400">£{stats.total_revenue?.toFixed(0)}</p>
                </div>
              </div>
            </div>
          )}

          {tab === 'autobiographies' && (
            <div className="fade-in">
              <h2 className="text-2xl font-bold mb-6">All Autobiographies ({data.length})</h2>
              <div className="bg-gray-800 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-gray-700 text-gray-400 text-xs uppercase">
                    <th className="px-4 py-3 text-left">Title</th><th className="px-4 py-3 text-left">Author</th><th className="px-4 py-3">Words</th><th className="px-4 py-3">Chapters</th><th className="px-4 py-3 text-right">Date</th>
                  </tr></thead>
                  <tbody>{data.map((a, i) => (
                    <tr key={i} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                      <td className="px-4 py-3 font-medium">{a.title}</td>
                      <td className="px-4 py-3 text-gray-400">{a.user_name}</td>
                      <td className="px-4 py-3 text-center">{a.word_count?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-center">{a.chapters}</td>
                      <td className="px-4 py-3 text-right text-gray-400">{formatDate(a.created_at)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'orders' && (
            <div className="fade-in">
              <h2 className="text-2xl font-bold mb-6">Hardcover Orders ({data.length})</h2>
              <div className="bg-gray-800 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-gray-700 text-gray-400 text-xs uppercase">
                    <th className="px-4 py-3 text-left">Order ID</th><th className="px-4 py-3 text-left">Customer</th><th className="px-4 py-3">Qty</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Date</th>
                  </tr></thead>
                  <tbody>{data.map((o, i) => (
                    <tr key={i} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                      <td className="px-4 py-3 font-mono text-xs">{o.order_id?.substring(0, 8)}</td>
                      <td className="px-4 py-3">{o.shipping_name}</td>
                      <td className="px-4 py-3 text-center">{o.quantity}</td>
                      <td className="px-4 py-3 text-center">£{(o.amount / 100).toFixed(0)}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs ${o.status === 'submitted_to_printer' ? 'bg-blue-500/20 text-blue-400' : o.status === 'pending_payment' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-600 text-gray-300'}`}>{o.lulu_status || o.status}</span></td>
                      <td className="px-4 py-3 text-right text-gray-400">{formatDate(o.created_at)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'gifts' && (
            <div className="fade-in">
              <h2 className="text-2xl font-bold mb-6">Gifts ({data.length})</h2>
              {data.length === 0 ? <p className="text-gray-500">No gifts yet.</p> : (
                <div className="bg-gray-800 rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-700 text-gray-400 text-xs uppercase">
                      <th className="px-4 py-3 text-left">Code</th><th className="px-4 py-3 text-left">From</th><th className="px-4 py-3 text-left">To</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Date</th>
                    </tr></thead>
                    <tbody>{data.map((g, i) => (
                      <tr key={i} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                        <td className="px-4 py-3 font-mono font-bold">{g.gift_code}</td>
                        <td className="px-4 py-3">{g.buyer_name}</td>
                        <td className="px-4 py-3">{g.recipient_name}</td>
                        <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs ${g.status === 'redeemed' ? 'bg-green-500/20 text-green-400' : g.status === 'paid' ? 'bg-blue-500/20 text-blue-400' : 'bg-yellow-500/20 text-yellow-400'}`}>{g.status}</span></td>
                        <td className="px-4 py-3 text-right text-gray-400">{formatDate(g.created_at)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === 'users' && (
            <div className="fade-in">
              <h2 className="text-2xl font-bold mb-6">Users ({data.length})</h2>
              {data.length === 0 ? <p className="text-gray-500">No users yet.</p> : (
                <div className="bg-gray-800 rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-700 text-gray-400 text-xs uppercase">
                      <th className="px-4 py-3 text-left">Name</th><th className="px-4 py-3 text-left">Email</th><th className="px-4 py-3 text-right">Joined</th>
                    </tr></thead>
                    <tbody>{data.map((u, i) => (
                      <tr key={i} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                        <td className="px-4 py-3 font-medium">{u.name}</td>
                        <td className="px-4 py-3 text-gray-400">{u.email}</td>
                        <td className="px-4 py-3 text-right text-gray-400">{formatDate(u.created_at)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-4 border-gray-600 border-t-purple-500 rounded-full animate-spin" />
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
